/**
 * Getting a photograph of a business card small enough to be useful.
 *
 * A modern iPhone photograph is four to eight megabytes. Three things go wrong
 * if one is used as it comes off the camera: the upload takes long enough on a
 * driveway's signal that the rep assumes it has failed, the base64 of it is
 * refused by the card reader's size check, and the model is charged for
 * resolution it cannot use — a card is 3.5 inches wide and 1600 pixels across
 * it is already more than the print carries.
 *
 * So: downscale to 1600 on the long edge, JPEG at 0.8, which lands a
 * photographed card around 200–350KB. That is small enough to upload over a
 * bad connection and large enough that 6pt grey type is still legible, which
 * is the constraint that actually matters.
 *
 * The EXIF orientation problem is handled by `createImageBitmap`, which applies
 * it. Drawing an `<img>` to a canvas does not, and a card photographed in
 * portrait would reach the model on its side — where it is read, badly, as a
 * card with vertical text.
 */

/** The long edge, in pixels, after downscaling. */
export const MAX_EDGE = 1600;

/** JPEG quality. High enough for small grey type on a white card. */
export const JPEG_QUALITY = 0.8;

export interface PreparedCard {
  /** The bytes to upload to Storage. */
  blob: Blob;
  /** The same bytes, base64, for the card reader. No data: prefix. */
  base64: string;
  /** An object URL for showing it on screen. The caller revokes it. */
  previewUrl: string;
  media_type: "image/jpeg";
}

/**
 * Read one file from the camera or the photo library into something sendable.
 *
 * Rejects rather than returns null on failure: every caller has to say
 * something to the rep, and a null would make that a second branch nobody
 * writes.
 */
export async function prepareCardImage(file: File): Promise<PreparedCard> {
  if (!file.type.startsWith("image/")) {
    throw new Error("That is not a photograph.");
  }

  // createImageBitmap applies EXIF orientation; drawing an <img> does not, and
  // a card photographed in portrait would reach the model on its side.
  const bitmap = await createImageBitmap(file).catch(() => {
    throw new Error("That photograph could not be opened.");
  });

  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) throw new Error("This browser cannot resize a photograph.");
  // A card is white. Without this, a source image with transparency draws onto
  // black and the model reads white text on a black card.
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
  );
  if (!blob) throw new Error("That photograph could not be resized.");

  return {
    blob,
    base64: await toBase64(blob),
    previewUrl: URL.createObjectURL(blob),
    media_type: "image/jpeg",
  };
}

/**
 * Base64 without the `data:` prefix.
 *
 * Via FileReader rather than by walking the bytes: a three-megabyte
 * `String.fromCharCode(...bytes)` overflows the call stack, which is a crash
 * that only appears on the largest photographs and therefore only in the
 * field.
 */
async function toBase64(blob: Blob): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () =>
      reject(new Error("That photograph could not be read."));
    reader.readAsDataURL(blob);
  });
  return dataUrl.replace(/^data:[^;]+;base64,/, "");
}
